'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Resource {
  id: number;
  type: 'guide' | 'calculator' | 'assessment';
  title: string;
  description: string;
  image: string;
  alt: string;
  downloadCount: string;
  icon: string;
  downloadUrl?: string;
}

interface ResourcesSectionProps {
  className?: string;
}

const ResourcesSection = ({ className = '' }: ResourcesSectionProps) => {
  const [selectedType, setSelectedType] = useState<'all' | 'guide' | 'calculator' | 'assessment'>('all');

  const resources: Resource[] = [
  {
    id: 1,
    type: 'calculator',
    title: "AI ROI Calculator",
    description: "Calculate potential revenue impact and cost savings from AI automation implementation with our interactive tool.",
    image: "https://images.unsplash.com/photo-1712640183722-ec59693f7c82",
    alt: "Business professional analyzing financial charts and ROI calculations on laptop screen",
    downloadCount: "2,847",
    icon: "CalculatorIcon"
  },
  {
    id: 2,
    type: 'guide',
    title: "AI Strategy Framework 2026",
    description: "Comprehensive guide to building an AI-first business strategy with proven frameworks and implementation roadmaps.",
    image: "https://images.unsplash.com/photo-1656646424501-06d57009b725",
    alt: "Modern office workspace with strategic planning documents and technology blueprints spread on desk",
    downloadCount: "5,234",
    icon: "DocumentTextIcon",
    downloadUrl: "https://hlygziqktowemwmpjwgx.supabase.co/storage/v1/object/public/rlx-resources/RLX%20-%20AI-Strategy-Framework-2026.pdf"
  },
  {
    id: 3,
    type: 'assessment',
    title: "AI Readiness Assessment",
    description: "Evaluate your organization's AI maturity level and identify priority areas for automation investment.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_146c9626d-1765954088482.png",
    alt: "Business team reviewing assessment checklist and organizational readiness metrics on tablet",
    downloadCount: "3,912",
    icon: "ClipboardDocumentCheckIcon"
  },
  {
    id: 4,
    type: 'guide',
    title: "Marketing Automation Playbook",
    description: "Step-by-step guide to implementing AI-powered marketing systems that drive 5x lead generation growth.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1997a6356-1764656421129.png",
    alt: "Marketing professional reviewing campaign analytics and automation workflows on multiple screens",
    downloadCount: "4,156",
    icon: "BookOpenIcon",
    downloadUrl: "https://hlygziqktowemwmpjwgx.supabase.co/storage/v1/object/public/rlx-resources/RLX%20-%20Marketing-Automation-Playbook.pdf"
  },
  {
    id: 5,
    type: 'calculator',
    title: "Support Cost Savings Calculator",
    description: "Estimate operational cost reductions and efficiency gains from AI-powered customer support automation.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19f3e14ff-1770142472977.png",
    alt: "Customer support manager analyzing cost reduction metrics and efficiency reports on dashboard",
    downloadCount: "1,923",
    icon: "CurrencyDollarIcon"
  },
  {
    id: 6,
    type: 'assessment',
    title: "Sales Pipeline Health Check",
    description: "Comprehensive assessment tool to identify revenue leakage points and optimization opportunities in your sales process.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ccbc698f-1773069917462.png",
    alt: "Sales executive reviewing pipeline analytics and conversion metrics on large monitor",
    downloadCount: "2,567",
    icon: "ChartBarSquareIcon"
  }];


  const filterTypes = [
  { value: 'all', label: 'All Resources', icon: 'SquaresPlusIcon' },
  { value: 'guide', label: 'Strategy Guides', icon: 'BookOpenIcon' },
  { value: 'calculator', label: 'ROI Calculators', icon: 'CalculatorIcon' },
  { value: 'assessment', label: 'Assessments', icon: 'ClipboardDocumentCheckIcon' }];


  const filteredResources = selectedType === 'all' ?
  resources :
  resources.filter((resource) => resource.type === selectedType);

  return (
    <section className={`bg-muted py-20 lg:py-32 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-card px-4 py-2 rounded-full mb-6">
            <Icon name="AcademicCapIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">Free Resources</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
            AI Strategy Resources & Tools
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Access our library of strategic frameworks, calculators, and assessment tools to accelerate your AI transformation journey.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {filterTypes.map((filter) =>
          <button
            key={filter.value}
            onClick={() => setSelectedType(filter.value as any)}
            className={`inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
            selectedType === filter.value ?
            'bg-primary text-white shadow-md' :
            'bg-card text-foreground hover:bg-card/80 border border-border'}`
            }>

              <Icon name={filter.icon as any} size={20} className="mr-2" />
              {filter.label}
            </button>
          )}
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredResources.map((resource) =>
          <div
            key={resource.id}
            className="group bg-card rounded-2xl overflow-hidden border border-border hover:border-primary hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-1">

              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <AppImage
                src={resource.image}
                alt={resource.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute top-4 right-4 bg-accent text-white px-3 py-1 rounded-full text-xs font-semibold">
                  {resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name={resource.icon as any} size={24} className="text-primary" />
                  <h3 className="text-xl font-heading font-bold text-foreground group-hover:text-primary transition-colors">
                    {resource.title}
                  </h3>
                </div>

                <p className="text-muted-foreground leading-relaxed mb-6">
                  {resource.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="ArrowDownTrayIcon" size={16} />
                    <span>{resource.downloadCount} views</span>
                  </div>
                  {resource.id === 1 || resource.id === 3 || resource.id === 5 ?
                <Link
                  href="/tools-assessments"
                  className="inline-flex items-center text-primary font-semibold hover:text-accent transition-colors">

                      View
                      <Icon name="ArrowRightIcon" size={18} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </Link> :
                resource.downloadUrl ?
                <a
                  href={resource.downloadUrl}
                  download="AI-Strategy-Framework-2026.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-primary font-semibold hover:text-accent transition-colors">

                      Download
                      <Icon name="ArrowDownTrayIcon" size={18} className="ml-1 group-hover:translate-y-0.5 transition-transform" />
                    </a> :

                <button className="inline-flex items-center text-primary font-semibold hover:text-accent transition-colors">
                      View
                      <Icon name="ArrowRightIcon" size={18} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </button>
                }
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Newsletter Signup */}
        <div className="mt-20 bg-gradient-to-br from-primary to-secondary rounded-2xl p-8 lg:p-12">
          <div className="max-w-3xl mx-auto text-center">
            <Icon name="EnvelopeIcon" size={48} className="text-white mx-auto mb-6" />
            <h3 className="text-2xl lg:text-3xl font-heading font-bold text-white mb-4">
              Get Weekly AI Strategy Insights
            </h3>
            <p className="text-white/90 text-lg mb-8">
              Join 15,000+ business leaders receiving actionable AI strategies, case studies, and industry trends every week.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-6 py-4 rounded-lg border-2 border-white/20 bg-white/10 backdrop-blur-sm text-white placeholder-white/60 focus:outline-none focus:border-white transition-colors" />

              <button
                type="submit"
                className="px-8 py-4 bg-accent text-white font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 whitespace-nowrap">

                Subscribe Now
              </button>
            </form>
            <p className="text-white/70 text-sm mt-4">
              No spam. Unsubscribe anytime. Your data is protected.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-primary text-white font-heading font-semibold rounded-lg hover:bg-primary/90 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-0.5">

            Schedule Strategy Session
            <Icon name="ArrowRightIcon" size={20} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>);

};

export default ResourcesSection;